import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { MdClose, MdShield } from 'react-icons/md'
import {
    getFolderAccess,
    setFolderAccess,
    type StorageFolder,
} from '@/lib/api/storage/storage-api'
import StorageRolePicker from './storage-role-picker'

interface Props {
    isOpen: boolean
    folder: StorageFolder | null
    onClose: () => void
    onSaved: () => void
}

/**
 * Edição dos perfis com acesso a uma pasta restrita.
 *
 * Desmarcar todos devolve a pasta ao modo comum — isso está escrito na tela
 * porque "restrita a nenhum perfil" seria uma pasta que ninguém abre.
 */
export default function StorageAccessModal({ isOpen, folder, onClose, onSaved }: Props) {
    const [roleIds, setRoleIds] = useState<number[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!isOpen || !folder) return

        setLoading(true)
        void (async () => {
            const response = await getFolderAccess(folder.id)
            setRoleIds(response.success ? (response.result ?? []) : [])
            setLoading(false)
        })()
    }, [isOpen, folder])

    if (!isOpen || !folder) return null

    const handleSave = async () => {
        setSaving(true)
        const response = await setFolderAccess(folder.id, roleIds)
        setSaving(false)

        if (!response.success) {
            toast.error(response.error || 'Não foi possível salvar os perfis de acesso')
            return
        }

        toast.success(
            roleIds.length
                ? 'Perfis de acesso atualizados'
                : 'A pasta voltou a ser visível para toda a empresa',
        )
        onSaved()
        onClose()
    }

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-md">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <MdShield className="text-xl text-primary" />
                        Perfis com acesso
                    </h3>
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost btn-circle"
                        onClick={onClose}
                        disabled={saving}
                    >
                        <MdClose className="text-xl" />
                    </button>
                </div>

                <p className="text-sm text-base-content/70 mb-5 truncate">{folder.name}</p>

                {loading ? (
                    <div className="flex justify-center py-6">
                        <span className="loading loading-spinner loading-md text-primary" />
                    </div>
                ) : (
                    <StorageRolePicker
                        selected={roleIds}
                        onChange={setRoleIds}
                        disabled={saving}
                    />
                )}

                {!loading && !roleIds.length && (
                    <div className="alert alert-warning py-2 mt-4">
                        <span className="text-sm">
                            Sem nenhum perfil marcado, a pasta deixa de ser restrita e volta a
                            ficar visível para toda a empresa.
                        </span>
                    </div>
                )}

                <div className="modal-action">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={onClose}
                        disabled={saving}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={saving || loading}
                    >
                        {saving && <span className="loading loading-spinner loading-sm" />}
                        Salvar
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </div>
    )
}
